import { useEffect, useMemo, useState, useCallback } from "react"
import { MULTI_SELECT_KEYS, OPTIONS } from "../../constants"
import { getSelectionsFromParams } from "../utils/queryParams"
import useSyncQueryParams from "./useSyncQueryParams"

function useModifiers(data) {
    const params = useMemo(() => new URLSearchParams(window.location.search), [])
    
    // Calculate actual bounds from data
    const { minCt, maxCt } = useMemo(() => {
        if (!data || data.length === 0) return { minCt: 0, maxCt: 3.0 }
        return {
            minCt: Math.min(...data.map(d => Number(d.ct))),
            maxCt: Math.max(...data.map(d => Number(d.ct)))
        }
    }, [data])

    // Create options object
    const options = useMemo(() => {
        const opt = {
            ct: { minValue: minCt, maxValue: maxCt }
        }
        MULTI_SELECT_KEYS.forEach(selector => {
            const optionsSet = new Set(data.map(d => d[selector]))
            opt[selector] = OPTIONS[selector].filter(option => optionsSet.has(option))
        })
        return opt
    }, [data, minCt, maxCt])

    const [selections, setSelections] = useState(() => getSelectionsFromParams(data, params))
    const [isInitialized, setIsInitialized] = useState(false)

    // Re-initialize selections once data is loaded (to apply actual bounds if no URL params were set)
    useEffect(() => {
        if (data && data.length > 0 && !isInitialized) {
            setSelections(getSelectionsFromParams(data, params))
            setIsInitialized(true)
        }
    }, [data, params, isInitialized])

    const resetSelections = useCallback(() => {
        setSelections(getSelectionsFromParams(data, new URLSearchParams()))
    }, [data])

    const actions = useMemo(() => ({
        reset: resetSelections
    }), [resetSelections])

    // Use our custom hook to sync the state back to the URL
    useSyncQueryParams(selections, minCt, maxCt, isInitialized)

    return { options, selections, setSelections, actions }
}

export default useModifiers;
